import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, Save, Lock, CheckCircle2, FileDown } from "lucide-react";

/**
 * Team-fillable canvas (BMC or VPC).  Instance is private to the team.
 * URL: /canvas/:slug
 */
export default function Canvas() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [template, setTemplate] = useState(null);
  const [instance, setInstance] = useState(null);
  const [sections, setSections] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const isStudent = user?.role === "student";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/canvas/templates");
        const t = (data.templates || []).find((x) => x.slug === slug);
        if (!t) { toast.error("Unknown canvas"); navigate("/resources"); return; }
        setTemplate(t);
        const { data: my } = await api.get("/canvas/instances/my");
        const inst = (my?.instances || {})[slug];
        setInstance(inst || { team_id: my?.team_id, slug, sections: {} });
        setSections(inst?.sections || {});
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load canvas");
      } finally { setLoading(false); }
    })();
  }, [slug]);

  const setSection = (k, v) => {
    setSections((s) => ({ ...s, [k]: v }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/canvas/instances/${slug}`, { sections });
      setInstance(data);
      setDirty(false);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-sm text-[#525252]">Loading canvas…</div>;
  if (!template) return null;

  if (isStudent && !user?.team_id) {
    return (
      <div className="border-2 border-dashed border-[#f4b847] bg-[#FEF3D0] p-8" data-testid="canvas-no-team">
        <Lock size={22} className="text-[#7A5B17]"/>
        <h2 className="font-display text-xl font-bold text-[#7A5B17] mt-3">Join a team first</h2>
        <p className="text-sm text-[#7A5B17] mt-1">Canvases are private to your team. Head to Sign-in &rarr; Enter join code.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid={`canvas-page-${slug}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Link to="/resources" className="text-xs uppercase tracking-wider text-[#25AAAA] hover:underline inline-flex items-center gap-1">
            <ArrowLeft size={12}/> Back to resources
          </Link>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2 text-[#0F1F2E]">{template.title}</h1>
          <p className="text-[#525252] mt-2 max-w-2xl text-sm">{template.subtitle}</p>
          {template.source_url && (
            <p className="text-[11px] text-[#525252] mt-2">
              Framework by <strong>{template.source_publisher}</strong> ·{" "}
              <a href={template.source_url} target="_blank" rel="noopener noreferrer"
                 className="text-[#25AAAA] hover:underline" data-testid="canvas-source-link">
                Official download &amp; support →
              </a>
            </p>
          )}
          <p className="text-[10px] uppercase tracking-[0.16em] text-[#25AAAA] mt-2 font-semibold">
            Private to your team{instance?.updated_by_name ? ` · last saved by ${instance.updated_by_name}` : ""}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {instance?.team_id && (
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/canvas/instances/${instance.team_id}/${slug}/pdf?auth=${encodeURIComponent(localStorage.getItem("weave_token") || "")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center h-10 px-4 border-2 border-[#0F1F2E] text-[#0F1F2E] font-semibold uppercase text-xs tracking-wider hover:bg-[#0F1F2E] hover:text-white transition-colors rounded-none"
              data-testid="canvas-pdf-btn"
            >
              <FileDown size={14} className="mr-2"/> Download PDF
            </a>
          )}
          <Button
            onClick={save}
            disabled={saving || !dirty}
            className="rounded-none bg-[#25AAAA] hover:bg-[#1e7d7d]"
            data-testid="canvas-save-btn">
            {dirty ? <><Save size={14} className="mr-2"/> {saving ? "Saving…" : "Save"}</> : <><CheckCircle2 size={14} className="mr-2"/> Saved</>}
          </Button>
        </div>
      </div>

      {/* Sections grid — 3-col for BMC (9 sections), 3-col for VPC (6 sections) */}
      <div className={`grid gap-0 border-l border-t border-[#E5E7EB] ${slug === "bmc" ? "md:grid-cols-3" : "md:grid-cols-3"}`} data-testid="canvas-sections">
        {template.sections.map((sec) => (
          <div key={sec.key} className="border-r border-b border-[#E5E7EB] bg-white p-4">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-1.5 h-1.5 bg-[var(--accent,#25AAAA)]" style={{ background: template.accent }}/>
              <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#525252]">{sec.label}</div>
            </div>
            <div className="text-[11px] text-[#525252] italic mb-2">{sec.prompt}</div>
            <Textarea
              value={sections[sec.key] || ""}
              onChange={(e) => setSection(sec.key, e.target.value)}
              placeholder="Add your thinking here…"
              className="rounded-none min-h-[110px] text-sm"
              data-testid={`canvas-section-${sec.key}`}
            />
          </div>
        ))}
      </div>

      <div className="border-l-4 border-[#25AAAA] bg-[#E6F7F7] px-4 py-3 text-sm text-[#1e7d7d]">
        Save as often as you want. Only your team, your admin and the mentors you&apos;ve booked can see this canvas.
      </div>
    </div>
  );
}
